import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { TrustBadges } from "./TrustBadges";
import { MagneticButton } from "@/components/common/MagneticButton";
import { scrollToSection } from "@/components/common/DeferredMount";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.45 + i * 0.12 },
  }),
};

export const HeroContent = () => (
  <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-start px-6 pt-40 pb-24 lg:px-0 lg:pt-52">
    <motion.span
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      data-testid={HERO.eyebrow}
      className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-accent"
    >
      AI-Powered Web Agency
    </motion.span>

    <h1
      data-testid={HERO.headline}
      className="font-display text-5xl font-semibold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-[6.5rem]"
    >
      <span className="block overflow-hidden">
        <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">
          We Build AI Powered
        </motion.span>
      </span>
      <span className="block overflow-hidden">
        <motion.span
          custom={1}
          variants={lineVariants}
          initial="hidden"
          animate="visible"
          className="block text-gradient-brand"
        >
          Websites That Grow.
        </motion.span>
      </span>
    </h1>

    <motion.p
      data-testid={HERO.subheadline}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
      className="mt-7 max-w-xl text-base text-white/72 sm:text-lg"
    >
      For clinics, hotels, real estate and premium brands who refuse to look
      ordinary online. We design, build and ship AI-native websites that turn
      visitors into booked calls.
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 1, ease: [0.16, 1, 0.3, 1] }}
      className="mt-9 flex flex-wrap items-center gap-4"
    >
      <MagneticButton strength={0.3}>
        <a
          href="#cta"
          onClick={(e) => { e.preventDefault(); scrollToSection("cta"); }}
          data-testid={HERO.primaryCta}
          className="group inline-flex items-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white shadow-glow-primary transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-glow-primary-lg active:scale-[0.98]"
        >
          Book a Call
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </a>
      </MagneticButton>
      <a
        href="#showcase"
        onClick={(e) => { e.preventDefault(); scrollToSection("showcase"); }}
        data-testid={HERO.secondaryCta}
        className="group inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:border-white/20 hover:bg-white/[0.06]"
      >
        <PlayCircle className="h-4 w-4 text-brand-accent" />
        Watch It In Action
      </a>
    </motion.div>

    <TrustBadges />
  </div>
);
